import {positiveMoney} from '../money.js';
import {supabase} from '../supabase/client.js';

export const EXPENSES_TABLE = 'expenses_1772928000000';
export const OWNER_CLAIMS_TABLE = 'owner_claims_1772928000000';
export const RECEIPTS_BUCKET = 'receipts';

function getReceiptPath(userId, businessId, fileName, expenseId) {
  const safeName = fileName.replace(/[^a-zA-Z0-9._-]/g, '-');
  return `${userId}/${businessId}/${expenseId}-${safeName}`;
}

async function uploadReceipt(userId, businessId, file, expenseId, client = supabase) {
  if (!client) {
    throw new Error('Supabase is not configured.');
  }

  const path = getReceiptPath(userId, businessId, file.name, expenseId);
  const {error} = await client.storage
    .from(RECEIPTS_BUCKET)
    .upload(path, file, {
      cacheControl: '3600',
      contentType: file.type || 'application/octet-stream',
      upsert: true
    });

  if (error) {
    throw new Error(`Receipt upload failed: ${error.message}`);
  }

  return path;
}

export async function getReceiptPreviewUrl(path) {
  if (!supabase || !path) {
    return null;
  }

  const {data, error} = await supabase.storage
    .from(RECEIPTS_BUCKET)
    .createSignedUrl(path, 300);

  if (error) {
    throw new Error(`Receipt preview failed: ${error.message}`);
  }

  return data?.signedUrl || null;
}

export async function listExpenses(userId, businessId) {
  if (!supabase) {
    throw new Error('Supabase is not configured.');
  }

  let query = supabase
    .from(EXPENSES_TABLE)
    .select('*, payment_method:payment_methods_1772932000000(*)')
    .eq('user_id', userId)
    .order('expense_date', {ascending: false})
    .order('created_at', {ascending: false});

  if (businessId) {
    query = query.eq('business_id', businessId);
  }

  const {data, error} = await query.limit(50);

  if (error) {
    throw error;
  }

  return data || [];
}

export async function getOwedToYou(userId, businessId) {
  if (!supabase) {
    throw new Error('Supabase is not configured.');
  }

  if (!userId || !businessId) return 0;
  const {data, error} = await supabase.rpc('get_owner_balance', {p_business_id: businessId});
  if (error) throw error;
  return Number(data || 0);
}

export async function countMissingReceipts(userId, businessId) {
  if (!supabase) {
    throw new Error('Supabase is not configured.');
  }

  let query = supabase
    .from(EXPENSES_TABLE)
    .select('id', {count: 'exact', head: true})
    .eq('user_id', userId)
    .is('receipt_url', null);

  if (businessId) {
    query = query.eq('business_id', businessId);
  }

  const {count, error} = await query;

  if (error) {
    throw error;
  }

  return count || 0;
}

export async function createExpense(userId, businessId, values, client = supabase) {
  if (!client) throw new Error('Supabase is not configured.');
  if (!userId || !businessId) throw new Error('Choose a Business before adding an expense.');
  if (!values.submissionId) throw new Error('An expense submission ID is required.');
  const amount = positiveMoney(values.amount);
  const file = values.receipt?.[0];
  // Upload first: a storage failure must not leave a saved expense without its claim.
  const receiptPath = file ? await uploadReceipt(userId, businessId, file, values.submissionId, client) : null;
  const rpc = values.recurringScheduleId ? 'create_recurring_expense_once' : 'create_expense_once';
  const params = {
    p_request_id: values.submissionId,
    p_business_id: businessId,
    p_amount: amount,
    p_merchant: values.merchant.trim(),
    p_expense_date: values.expenseDate,
    p_category: values.category,
    p_notes: values.notes?.trim() || null,
    p_payment_method_id: values.paymentMethodId || null,
    p_receipt_url: receiptPath
  };
  if (values.recurringScheduleId) {
    params.p_schedule_id = values.recurringScheduleId;
    params.p_due_date = values.recurringDueDate;
  }
  const {data, error} = await client.rpc(rpc, params);
  if (error) throw error;
  return data;
}

export async function updateExpense(expenseId, userId, businessId, values) {
  if (!supabase) {
    throw new Error('Supabase is not configured.');
  }

  if (!expenseId || !userId || !businessId) {
    throw new Error('Expense, user, and Business details are required.');
  }

  const selectedFile = values.receipt?.[0] || null;
  const paymentMethodId = values.paymentMethodId || null;
  const amount = positiveMoney(values.amount);
  let receiptPath = values.existingReceiptUrl || null;

  if (selectedFile) {
    receiptPath = await uploadReceipt(userId, businessId, selectedFile, expenseId);
  }

  const {data: expense, error: expenseError} = await supabase
    .from(EXPENSES_TABLE)
    .update({
      merchant: values.merchant.trim(),
      amount,
      expense_date: values.expenseDate,
      category: values.category,
      notes: values.notes?.trim() || null,
      payment_method_id: paymentMethodId,
      receipt_url: receiptPath,
      status: receiptPath ? 'pending' : 'missing_receipt'
    })
    .eq('id', expenseId)
    .eq('user_id', userId)
    .eq('business_id', businessId)
    .select('*, payment_method:payment_methods_1772932000000(*)')
    .single();

  if (expenseError) {
    throw expenseError;
  }

  // The database trigger synchronizes the claim atomically with this update.
  return expense;
}

export async function attachMissingReceipt(userId,businessId,expenseId,file,client=supabase) {
 if(!file || file.size>10*1024*1024 || !['image/jpeg','image/png','image/webp','application/pdf'].includes(file.type)) throw new Error('Choose a JPG, PNG, WebP or PDF up to 10 MB.');
 const receiptPath=await uploadReceipt(userId,businessId,file,expenseId,client);
 const {data,error}=await client.from(EXPENSES_TABLE).update({receipt_url:receiptPath,status:'pending'})
  .eq('id',expenseId).eq('business_id',businessId).eq('user_id',userId).is('receipt_url',null).select().single();
 if(error) throw new Error('The receipt could not be attached. Refresh and check whether it was already added.');
 return data;
}
